
import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, MapPin, Video, User, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getTimezoneDisplayWithOffset } from '@/utils/timezone-detection';
import { STANDARD_SESSION_FORMATS, normalizeSessionFormat } from '@/utils/session-format-helpers';
import { useLocalCurrency } from '@/hooks/useLocalCurrency';

interface BookingDetailsProps {
  bookingInfo: {
    practitioner: {
      first_name: string;
      last_name: string;
      professional_title?: string;
      avatar_url?: string;
    };
    service: {
      service_name: string;
      duration_minutes: number;
      price: number;
      currency: string;
    };
    dateTime: Date;
    format: 'virtual' | 'in_person' | 'online'; // Support both 'virtual' (standard) and 'online' (legacy)
    location?: string;
    notes?: string;
    practitionerTimezone?: string;
  };
  hidePrice?: boolean;
}

const BookingDetails: React.FC<BookingDetailsProps> = ({ bookingInfo, hidePrice = false }) => {
  const { t } = useTranslation('booking');
  const { practitioner, service, dateTime, format: sessionFormat, location, notes, practitionerTimezone } = bookingInfo;
  const { userCurrency, formatLocal, isLoading: ratesLoading } = useLocalCurrency();

  const practitionerName = `${practitioner.first_name} ${practitioner.last_name}`;

  // Use practitioner's timezone for time display
  const timezone = practitionerTimezone || 'America/New_York';
  const timezoneDisplay = getTimezoneDisplayWithOffset(timezone);

  // Normalize format to handle both 'online' and 'virtual'
  const normalizedFormat = normalizeSessionFormat(sessionFormat);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg sm:text-xl">{t('bookingSummary')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Practitioner Info */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pb-3 border-b">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {practitioner.avatar_url ? (
              <img 
                src={practitioner.avatar_url} 
                alt={practitionerName}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">{practitionerName}</h3>
              {practitioner.professional_title && (
                <p className="text-xs sm:text-sm text-gray-600 truncate">{practitioner.professional_title}</p>
              )}
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="space-y-3">
          <div>
            <h4 className="font-medium text-gray-900 text-sm sm:text-base mb-2">{service.service_name}</h4>
          </div>

          {/* Date & Time */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-sm sm:text-base text-gray-700 truncate">
                {formatInTimeZone(dateTime, timezone, 'EEEE, MMMM d, yyyy')}
              </span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-sm sm:text-base text-gray-700">
                {timezoneDisplay}
              </span>
            </div>
          </div>

          {/* Location/Format - Improved mobile layout */}
          <div className="flex items-start gap-2 min-w-0">
            {normalizedFormat === STANDARD_SESSION_FORMATS.VIRTUAL ? (
              <>
                <Video className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm sm:text-base text-gray-700">{t('onlineSession')}</span>
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-sm sm:text-base text-gray-700 break-words leading-relaxed">
                    {location || t('inPersonSession')}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Price */}
          {!hidePrice && (
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <div className="flex flex-col">
                {service.currency.toUpperCase() !== userCurrency.toUpperCase() && !ratesLoading ? (
                  <>
                    <span className="text-sm sm:text-base text-gray-700">
                      {formatLocal(service.price, service.currency)}
                    </span>
                    <span className="text-xs text-gray-500">
                      ≈ {new Intl.NumberFormat('en', { style: 'currency', currency: service.currency }).format(service.price)} {service.currency}
                    </span>
                  </>
                ) : (
                  <span className="text-sm sm:text-base text-gray-700">
                    {new Intl.NumberFormat('en', { style: 'currency', currency: service.currency }).format(service.price)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {notes && (
            <div className="pt-3 border-t">
              <h5 className="font-medium text-gray-900 text-sm mb-2">{t('yourNotes')}</h5>
              <p className="text-xs sm:text-sm text-gray-600 break-words leading-relaxed">{notes}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingDetails;
